import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Check, Clock } from "lucide-react";
import { API } from "./_apiConfig";

const TestimonialsManagement = () => {
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    company: "",
    content: "",
    image: "",
    rating: 5,
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([fetchTestimonials(), fetchPending()]);
    setLoading(false);
  };

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API}/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const fetchPending = async () => {
    try {
      const response = await fetch(`${API}/admin/testimonials/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingReviews(data);
      }
    } catch (error) {
      console.error("Error fetching pending reviews:", error);
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await fetch(`${API}/admin/testimonials/${id}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Avis approuvé et publié");
        fetchAll();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur");
      }
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error("Erreur");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API}/testimonials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Témoignage ajouté !");
        setIsDialogOpen(false);
        resetForm();
        fetchAll();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur");
      }
    } catch (error) {
      console.error("Error saving testimonial:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;

    try {
      const response = await fetch(`${API}/testimonials/${testimonialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Témoignage supprimé");
        fetchAll();
      }
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      company: "",
      content: "",
      image: "",
      rating: 5,
    });
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
          Gestion des Témoignages
          {pendingReviews.length > 0 && (
            <Badge className="ml-3 bg-yellow-500">
              {pendingReviews.length} en attente
            </Badge>
          )}
        </h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="new-testimonial-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Témoignage
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau témoignage</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rôle *</Label>
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="CEO, CTO, Manager..."
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Entreprise</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Témoignage *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={4}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Photo URL</Label>
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note (1-5)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="save-testimonial-btn">
                Ajouter
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Avis en attente */}
          {pendingReviews.length > 0 && (
            <div className="mb-8">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                Avis en attente de validation ({pendingReviews.length})
              </h3>
              <div className="space-y-3">
                {pendingReviews.map((review) => (
                  <Card key={review.id} className="border-yellow-500/30" data-testid={`pending-review-${review.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-medium text-foreground">{review.name}</span>
                            <span className="text-sm text-muted-foreground">
                              {review.role}{review.company && ` @ ${review.company}`}
                            </span>
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className={i < review.rating ? "text-yellow-500" : "text-gray-300"}>
                                  ★
                                </span>
                              ))}
                            </div>
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                              En attente
                            </Badge>
                          </div>
                          <p className="text-muted-foreground italic">"{review.content}"</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Soumis le {new Date(review.created_at).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(review.id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-review-${review.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(review.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            data-testid={`reject-review-${review.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Avis publiés */}
          {testimonials.length > 0 ? (
            <>
              {pendingReviews.length > 0 && (
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  Avis publiés ({testimonials.length})
                </h3>
              )}
              <div className="grid gap-4">
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-foreground">{testimonial.name}</span>
                            <span className="text-sm text-muted-foreground">
                              {testimonial.role}{testimonial.company && ` @ ${testimonial.company}`}
                            </span>
                            <div className="flex gap-0.5 ml-2">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className={i < testimonial.rating ? "text-yellow-500" : "text-gray-300"}>
                                  ★
                                </span>
                              ))}
                            </div>
                          </div>
                          <p className="text-muted-foreground italic">"{testimonial.content}"</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(testimonial.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : pendingReviews.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Aucun témoignage. Ajoutez votre premier témoignage !
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default TestimonialsManagement;
